/**
 * Pexels API Service
 *
 * Provides search functionality for stock photos and videos from Pexels.
 * Attribution is required when using Pexels media: "Photo by [Photographer] on Pexels"
 *
 * Media layer (2026-11-04): the calls go through the gateway's
 * /api/media/pexels proxy, which holds the server's key — the key no longer
 * ships inside this bundle. The proxy returns Pexels' JSON unchanged, so the
 * endpoint strings and the parsing below are the ones Pexels documents.
 */

import { authedFetch } from '../utils/authedFetch';

const PEXELS_PROXY = '/api/media/pexels';

// Photo interfaces
export interface PexelsPhotoSrc {
  original: string;
  large2x: string;
  large: string;
  medium: string;
  small: string;
  portrait: string;
  landscape: string;
  tiny: string;
}

export interface PexelsPhoto {
  id: number;
  width: number;
  height: number;
  url: string;
  photographer: string;
  photographer_url: string;
  photographer_id: number;
  avg_color: string;
  src: PexelsPhotoSrc;
  liked: boolean;
  alt: string;
}

export interface PexelsPhotosResponse {
  page: number;
  per_page: number;
  photos: PexelsPhoto[];
  total_results: number;
  next_page?: string;
  prev_page?: string;
}

// Video interfaces
export interface PexelsVideoFile {
  id: number;
  quality: string;
  file_type: string;
  width: number;
  height: number;
  fps: number;
  link: string;
}

export interface PexelsVideoPicture {
  id: number;
  picture: string;
  nr: number;
}

export interface PexelsVideoUser {
  id: number;
  name: string;
  url: string;
}

export interface PexelsVideo {
  id: number;
  width: number;
  height: number;
  url: string;
  image: string; // Thumbnail
  duration: number;
  user: PexelsVideoUser;
  video_files: PexelsVideoFile[];
  video_pictures: PexelsVideoPicture[];
}

export interface PexelsVideosResponse {
  page: number;
  per_page: number;
  videos: PexelsVideo[];
  total_results: number;
  next_page?: string;
  prev_page?: string;
}

// Combined media type for unified handling
export type PexelsMediaType = 'photo' | 'video';

export interface PexelsMediaItem {
  id: number;
  type: PexelsMediaType;
  thumbnailUrl: string;
  fullUrl: string;
  width: number;
  height: number;
  photographer: string;
  photographerUrl: string;
  attribution: string; // "Photo by X on Pexels" or "Video by X on Pexels"
  duration?: number; // Video duration in seconds (only for videos)
}

/**
 * Convert PexelsPhoto to unified PexelsMediaItem
 */
function photoToMediaItem(photo: PexelsPhoto): PexelsMediaItem {
  return {
    id: photo.id,
    type: 'photo',
    thumbnailUrl: photo.src.medium,
    fullUrl: photo.src.large,
    width: photo.width,
    height: photo.height,
    photographer: photo.photographer,
    photographerUrl: photo.photographer_url,
    attribution: `Photo by ${photo.photographer} on Pexels`,
  };
}

/**
 * Convert PexelsVideo to unified PexelsMediaItem
 */
function videoToMediaItem(video: PexelsVideo): PexelsMediaItem {
  // Get the best quality video file (prefer HD)
  const bestFile = video.video_files
    .filter(f => f.quality === 'hd' || f.quality === 'sd')
    .sort((a, b) => b.width - a.width)[0] || video.video_files[0];

  return {
    id: video.id,
    type: 'video',
    thumbnailUrl: video.image,
    fullUrl: bestFile?.link || video.video_files[0]?.link || '',
    width: video.width,
    height: video.height,
    photographer: video.user.name,
    photographerUrl: video.user.url,
    attribution: `Video by ${video.user.name} on Pexels`,
    duration: video.duration, // Include video duration in seconds
  };
}

/**
 * Call Pexels through the gateway proxy (the server holds the key).
 */
async function pexelsFetch<T>(endpoint: string): Promise<T> {
  const response = await authedFetch(`${PEXELS_PROXY}${endpoint}`);

  if (!response.ok) {
    let detail = '';
    try {
      detail = (await response.json())?.detail || '';
    } catch {
      detail = '';
    }
    throw new Error(detail || `Pexels API error: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

/**
 * Search for photos on Pexels
 */
export async function searchPhotos(
  query: string,
  options?: { page?: number; perPage?: number }
): Promise<{ photos: PexelsMediaItem[]; totalResults: number; hasMore: boolean }> {
  const page = options?.page || 1;
  const perPage = options?.perPage || 12;

  const params = new URLSearchParams({
    query,
    page: String(page),
    per_page: String(perPage),
  });

  const response = await pexelsFetch<PexelsPhotosResponse>(
    `/v1/search?${params}`
  );

  return {
    photos: response.photos.map(photoToMediaItem),
    totalResults: response.total_results,
    hasMore: response.next_page !== undefined,
  };
}

/**
 * Search for videos on Pexels
 */
export async function searchVideos(
  query: string,
  options?: { page?: number; perPage?: number }
): Promise<{ videos: PexelsMediaItem[]; totalResults: number; hasMore: boolean }> {
  const page = options?.page || 1;
  const perPage = options?.perPage || 12;

  const params = new URLSearchParams({
    query,
    page: String(page),
    per_page: String(perPage),
  });

  const response = await pexelsFetch<PexelsVideosResponse>(
    `/videos/search?${params}`
  );

  return {
    videos: response.videos.map(videoToMediaItem),
    totalResults: response.total_results,
    hasMore: response.next_page !== undefined,
  };
}

/**
 * Get curated photos (for initial display when no search query)
 */
export async function getCuratedPhotos(
  options?: { page?: number; perPage?: number }
): Promise<{ photos: PexelsMediaItem[]; hasMore: boolean }> {
  const page = options?.page || 1;
  const perPage = options?.perPage || 12;

  const params = new URLSearchParams({
    page: String(page),
    per_page: String(perPage),
  });

  const response = await pexelsFetch<PexelsPhotosResponse>(
    `/v1/curated?${params}`
  );

  return {
    photos: response.photos.map(photoToMediaItem),
    hasMore: response.next_page !== undefined,
  };
}

/**
 * Get popular videos (for initial display when no search query)
 */
export async function getPopularVideos(
  options?: { page?: number; perPage?: number }
): Promise<{ videos: PexelsMediaItem[]; hasMore: boolean }> {
  const page = options?.page || 1;
  const perPage = options?.perPage || 12;

  const params = new URLSearchParams({
    page: String(page),
    per_page: String(perPage),
  });

  const response = await pexelsFetch<PexelsVideosResponse>(
    `/videos/popular?${params}`
  );

  return {
    videos: response.videos.map(videoToMediaItem),
    hasMore: response.next_page !== undefined,
  };
}

/**
 * Search both photos and videos, returning combined results
 */
export async function searchMedia(
  query: string,
  mediaType: 'photos' | 'videos' | 'both',
  options?: { page?: number; perPage?: number }
): Promise<{ media: PexelsMediaItem[]; hasMore: boolean }> {
  const perPage = options?.perPage || 6;
  const page = options?.page || 1;

  if (mediaType === 'photos') {
    const result = await searchPhotos(query, { page, perPage });
    return { media: result.photos, hasMore: result.hasMore };
  }

  if (mediaType === 'videos') {
    const result = await searchVideos(query, { page, perPage });
    return { media: result.videos, hasMore: result.hasMore };
  }

  // Both: fetch half of each type
  const halfPerPage = Math.ceil(perPage / 2);
  const [photosResult, videosResult] = await Promise.all([
    searchPhotos(query, { page, perPage: halfPerPage }),
    searchVideos(query, { page, perPage: halfPerPage }),
  ]);

  // Interleave photos and videos
  const combined: PexelsMediaItem[] = [];
  const maxLen = Math.max(photosResult.photos.length, videosResult.videos.length);
  for (let i = 0; i < maxLen; i++) {
    if (i < photosResult.photos.length) combined.push(photosResult.photos[i]);
    if (i < videosResult.videos.length) combined.push(videosResult.videos[i]);
  }

  return {
    media: combined.slice(0, perPage),
    hasMore: photosResult.hasMore || videosResult.hasMore,
  };
}

/**
 * Get initial media for display (curated photos and popular videos)
 */
export async function getInitialMedia(
  mediaType: 'photos' | 'videos' | 'both',
  options?: { page?: number; perPage?: number }
): Promise<{ media: PexelsMediaItem[]; hasMore: boolean }> {
  const perPage = options?.perPage || 12;
  const page = options?.page || 1;

  if (mediaType === 'photos') {
    const result = await getCuratedPhotos({ page, perPage });
    return { media: result.photos, hasMore: result.hasMore };
  }

  if (mediaType === 'videos') {
    const result = await getPopularVideos({ page, perPage });
    return { media: result.videos, hasMore: result.hasMore };
  }

  // Both: fetch half of each type
  const halfPerPage = Math.ceil(perPage / 2);
  const [photosResult, videosResult] = await Promise.all([
    getCuratedPhotos({ page, perPage: halfPerPage }),
    getPopularVideos({ page, perPage: halfPerPage }),
  ]);

  // Interleave photos and videos
  const combined: PexelsMediaItem[] = [];
  const maxLen = Math.max(photosResult.photos.length, videosResult.videos.length);
  for (let i = 0; i < maxLen; i++) {
    if (i < photosResult.photos.length) combined.push(photosResult.photos[i]);
    if (i < videosResult.videos.length) combined.push(videosResult.videos[i]);
  }

  return {
    media: combined.slice(0, perPage),
    hasMore: photosResult.hasMore || videosResult.hasMore,
  };
}
